/**
 * smoke_detected anomaly detail panel (L.2.2, §6.2). An uncorroborated camera
 * smoke detection awaiting operator assessment — NOT scored (no Response Level),
 * shown with a distinct "pending operator assessment" badge. Two operator actions:
 *   • Confirm Smoke (rose)  → promotes to a high_risk_hotspot; the new hotspot
 *                             enters the normal queue.
 *   • Dismiss Smoke (slate) → logged false positive; auto-leaves the queue.
 */
import { useEffect, useState } from 'react';
import {
  confirmSmokeAnomaly,
  dismissSmokeAnomaly,
  type SmokeAnomalyFeature,
} from './api';
import { SMOKE_ANOMALY_COLOR, SMOKE_PENDING_BADGE_LABEL } from './smokeAnomalyStyle';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { RequestLiveEyes } from './RequestLiveEyes';
import { smokeToQueueItem } from './anomalyQueue';
import { AnomalyAckButton } from './AnomalyAckButton';
import { AnomalyAssessmentControl } from './AnomalyAssessmentControl';

function agoLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

export function SmokeAnomalyBody({
  anomaly,
  onResolved,
}: {
  anomaly: SmokeAnomalyFeature;
  /** Called after a confirm/dismiss so the parent refreshes the queue + clears
   *  the selection. `promotedHotspotId` is set when confirmed. */
  onResolved?: (outcome: 'confirmed' | 'dismissed', promotedHotspotId?: string) => void;
}): JSX.Element {
  const p = anomaly.properties;
  const [lon, lat] = anomaly.geometry.coordinates;
  const [busy, setBusy] = useState<'confirm' | 'dismiss' | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    logAction('view_smoke_detection_anomaly_detail', {
      anomaly_id: anomaly.id,
      camera_id: p.camera_id,
    });
  }, [anomaly.id, p.camera_id]);

  const confirm = async (): Promise<void> => {
    setBusy('confirm');
    try {
      const sessionId = getSessionId();
      logAction('confirm_smoke_detection', { anomaly_id: anomaly.id, camera_id: p.camera_id });
      const res = await confirmSmokeAnomaly(anomaly.id, sessionId);
      showToast('Smoke confirmed — promoted to a high-risk hotspot. Now in the queue.', {
        variant: 'success',
      });
      onResolved?.('confirmed', res.hotspot_id);
    } catch {
      showToast('Could not confirm smoke detection.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (): Promise<void> => {
    setBusy('dismiss');
    try {
      const sessionId = getSessionId();
      logAction('dismiss_smoke_detection', { anomaly_id: anomaly.id, reason: reason || null });
      await dismissSmokeAnomaly(anomaly.id, sessionId, reason || undefined);
      showToast('Smoke dismissed — logged as a false positive. Left the queue.', {
        variant: 'success',
      });
      onResolved?.('dismissed');
    } catch {
      showToast('Could not dismiss smoke detection.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4" data-testid="smoke-anomaly-body">
      <div className="border-l-2 pl-3" style={{ borderColor: SMOKE_ANOMALY_COLOR }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Smoke detected</p>
        <h2 className="text-base font-semibold">Camera smoke — {p.camera_name}</h2>
        <span
          className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${SMOKE_ANOMALY_COLOR}22`, color: SMOKE_ANOMALY_COLOR }}
        >
          {SMOKE_PENDING_BADGE_LABEL}
        </span>
        {/* #391 — inline ✓ Ack (triage demote), reusing the queue's unified-workstate
            path. Unscored "needs assessment" item ⇒ always ackable while unacked;
            coexists with the Confirm/Dismiss resolution actions below. */}
        <div className="mt-2">
          <AnomalyAckButton item={smokeToQueueItem(anomaly)} />
        </div>
        {/* #331 — the reversible ASSESSMENT axis (unconfirmed ⇄ confirmed ⇄ false
            positive). Distinct from ✓ Ack above and from the "Confirm Smoke" PROMOTION
            below (which raises a hotspot). Closes the unrecoverable-dismiss hole. */}
        <div className="mt-2">
          <AnomalyAssessmentControl
            source="smoke"
            sourceId={anomaly.id}
            initialAssessment={p.assessment ?? 'unconfirmed'}
          />
        </div>
      </div>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Detection</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Camera</dt>
          <dd>{p.camera_name}</dd>
          {p.camera_source && (
            <>
              <dt className="text-slate-400">Source</dt>
              <dd>{p.camera_source}</dd>
            </>
          )}
          <dt className="text-slate-400">Detected</dt>
          <dd>{agoLabel(p.detected_at)}</dd>
          <dt className="text-slate-400">Confidence</dt>
          <dd className="font-mono">{p.confidence != null ? `${p.confidence} / 100` : '—'}</dd>
          {p.model && (
            <>
              <dt className="text-slate-400">Model</dt>
              <dd className="font-mono text-xs">{p.model}</dd>
            </>
          )}
          {p.ptz_azimuth_deg != null && (
            <>
              <dt className="text-slate-400">Aim</dt>
              <dd className="font-mono">{Math.round(p.ptz_azimuth_deg)}°</dd>
            </>
          )}
          <dt className="text-slate-400">Position</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
        <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
          A camera flagged smoke with no corroborating satellite, air-quality, plume,
          reported-fire, or fire-weather signal nearby. Camera-never-alone for a scored
          anomaly — it needs operator eyes before it escalates.
        </p>
      </section>

      <section className="border-t border-slate-800 pt-3 space-y-2">
        <label className="block text-[11px] text-slate-400" htmlFor="smoke-dismiss-reason">
          Dismiss reason (optional)
        </label>
        <input
          id="smoke-dismiss-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. fog / cloud / dust"
          className="w-full rounded bg-slate-800 border border-slate-700 px-2 py-1 text-sm"
        />
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void confirm()}
            className="flex-1 rounded bg-rose-600 hover:bg-rose-500 disabled:opacity-50 px-3 py-2 text-sm font-medium"
          >
            {busy === 'confirm' ? 'Confirming…' : 'Confirm Smoke'}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void dismiss()}
            className="flex-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-2 text-sm font-medium"
          >
            {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss Smoke'}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Confirm promotes this to a high-risk hotspot (camera-smoke corroboration);
          dismiss logs a false positive. Both are audited (no hard delete).
        </p>
      </section>

      {/* #348b — drone marketplace front door, anchored on this detection. */}
      <RequestLiveEyes ao={{ lat, lon }} aoLabel={`camera smoke — ${p.camera_name}`} />
    </div>
  );
}
